// Vocabulary loader for AAC prediction
import enRaw from "./en_50k.txt?raw";
import heRaw from "./he_50k.txt?raw";
import ruRaw from "./ru_50k.txt?raw";
import arRaw from "./ar_50k.txt?raw";

export type SupportedLanguage = "en" | "he" | "ru" | "ar";

/**
 * Parse frequency list txt file format: "word count" per line
 */
function parseTxtVocabulary(raw: string): string[] {
  return raw
    .split("\n")
    .map((line) => line.trim().split(" ")[0])
    .filter((word) => word && word.length > 1 && !/^[0-9]+$/.test(word));
}

// Parse and cache vocabularies
const vocabularies: Record<SupportedLanguage, string[]> = {
  en: parseTxtVocabulary(enRaw),
  he: parseTxtVocabulary(heRaw),
  ru: parseTxtVocabulary(ruRaw),
  ar: parseTxtVocabulary(arRaw),
};

/**
 * Get vocabulary words for a specific language
 */
export function getVocabulary(lang: SupportedLanguage): string[] {
  return vocabularies[lang] ?? [];
}

/**
 * Detect language from text based on character patterns
 */
export function detectLanguage(text: string): SupportedLanguage {
  const hebrewPattern = /[\u0590-\u05FF]/;
  const arabicPattern = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
  const russianPattern = /[\u0400-\u04FF]/;

  if (hebrewPattern.test(text)) return "he";
  if (arabicPattern.test(text)) return "ar";
  if (russianPattern.test(text)) return "ru";
  return "en";
}

/**
 * Search vocabulary for words starting with a prefix
 */
export function searchVocabulary(
  prefix: string,
  lang: SupportedLanguage,
  limit: number = 6
): string[] {
  if (!prefix.trim()) return [];

  const words = getVocabulary(lang);
  const lowerPrefix = prefix.toLowerCase();

  return words
    .filter((word) => word.toLowerCase().startsWith(lowerPrefix) && word !== prefix)
    .slice(0, limit);
}

/**
 * Get all supported languages
 */
export function getSupportedLanguages(): SupportedLanguage[] {
  return Object.keys(vocabularies) as SupportedLanguage[];
}

/**
 * Check if a language is supported
 */
export function isLanguageSupported(lang: string): lang is SupportedLanguage {
  return lang in vocabularies;
}
